import type { AIPresenceState } from '@/common/components/presence/types';
import {
  getNebulaChoreography,
  getNebulaMotionProfile,
  resolveSpeakingContourRoughness,
  resolveSpeakingEnergy,
} from '@/common/components/presence/nebula/nebulaChoreography';
import type { OrbPaletteStops } from '@/common/theme/orbPalette';

export const NEBULA_STATIC_PHASE_MS = 1840;

export type CloudFamily = 'cloudA' | 'cloudB' | 'cloudC';

export type NebulaFormation = {
  id: string;
  cloudFamily: CloudFamily;
  /** Normalized offset from orb centre (−1…1). */
  centerX: number;
  centerY: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  alpha: number;
  phase: number;
  seed: number;
  contourRoughness: number;
  /** 0…1 paint-stack key — back layers are drawn first; this slowly crosses. */
  depth: number;
  /** −1…1 reshape amount consumed by lobe geometry (not rigid translation). */
  morph: number;
  /**
   * ±1 orbit handedness. Neighbouring formations counter-rotate so the field
   * never resolves into one readable sweep direction.
   */
  swirl: number;
};

export type NebulaSpecular = {
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
  alpha: number;
};

export type NebulaVisualFrame = {
  formations: NebulaFormation[];
  silhouetteRadius: number;
  wellCenterX: number;
  wellCenterY: number;
  wellAlpha: number;
  /** White mix applied to the cool well before painting; preserves depth without black dominance. */
  wellColorLift: number;
  /** State-specific lift for tenant cloud paints; active work is intentionally brighter than idle. */
  cloudColorLift: number;
  innerShadowStrength: number;
  coreAlpha: number;
  specular: NebulaSpecular;
  rimAlpha: number;
  haloAlpha: number;
  luminosity: number;
  elapsedMs: number;
  isCompact: boolean;
};

export type BuildNebulaFrameParams = {
  state: AIPresenceState;
  size: number;
  elapsedMs: number;
  pulseBoost?: number;
  /** Examiner speech envelope amp — drives speaking disturbance only. */
  speechAmp?: number;
};

type Rgb = [number, number, number];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseHexRgb(hex: string): Rgb {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

function rgbToHsl(rgb: Rgb): { h: number; s: number; l: number } {
  const [r, g, b] = rgb.map((channel) => channel / 255) as Rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;

  if (delta > 0) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * (((b - r) / delta) + 2);
    else h = 60 * (((r - g) / delta) + 4);
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h: (h + 360) % 360, s, l };
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r1, g1, b1] = hp < 1 ? [c, x, 0]
    : hp < 2 ? [x, c, 0]
      : hp < 3 ? [0, c, x]
        : hp < 4 ? [0, x, c]
          : hp < 5 ? [x, 0, c]
            : [c, 0, x];
  const m = l - c / 2;
  return [r1, g1, b1].map((channel) => Math.round((channel + m) * 255)) as Rgb;
}

/**
 * Renderer-facing tenant colour: preserve hue identity but avoid the high
 * lightness that screen blending previously washed into a white core.
 */
export function getNebulaFormationPaintRgb(hex: string): Rgb {
  const { h, s } = rgbToHsl(parseHexRgb(hex));
  return hslToRgb(h, Math.max(0.56, Math.min(s, 0.66)), 0.6);
}

/**
 * The depth the well is graded toward. Mixing the well toward white turns a
 * cool navy into flat grey — the interior must stay *coloured* darkness so the
 * outer field reads as atmosphere you look through, not as an empty backing.
 *
 * Its lightness carries most of the state ladder: cloud opacity alone cannot
 * separate states, because a brighter cloud over a brighter backing nets out.
 */
export function nebulaWellAtmosphereRgb(palette: OrbPaletteStops, luminosity: number): Rgb {
  const { h, s } = rgbToHsl(parseHexRgb(palette.cloudC));
  return hslToRgb(h, Math.max(0.5, Math.min(s, 0.72)), 0.09 + clamp(luminosity, 0, 1) * 0.17);
}

type FormationTemplate = {
  id: string;
  family: CloudFamily;
  bx: number;
  by: number;
  sx: number;
  sy: number;
  /** ±1 orbit handedness — see NebulaFormation.swirl. */
  spin: number;
  seed: number;
  rough: number;
  alphaBias: number;
  periodMs: number;
  periodMsY: number;
  swayPeriodMs: number;
};

const FORMATION_CONTAINMENT = 0.93;

/** Slow tour amplitude, so every region of the glass eventually holds fog. */
const FORMATION_ROAM = 0.2;

/**
 * Deliberately unequal parcels, painted back to front.
 *
 * Four similar discs at four similar radii are *countable* — that is the
 * "cotton balls in a jar" read. So `a` is an envelope wider than the shell:
 * its own outline is clipped away, leaving continuous fog with no edge to
 * find. The rest are markedly different sizes and radii, so they register as
 * densities inside that fog rather than as peers of it.
 */
const FULL_TEMPLATES: FormationTemplate[] = [
  {
    id: 'a',
    family: 'cloudC',
    bx: -0.04,
    by: -0.02,
    sx: 1.68,
    sy: 1.54,
    spin: 1,
    seed: 7,
    rough: 0.5,
    alphaBias: -0.16,
    periodMs: 9200,
    periodMsY: 11300,
    swayPeriodMs: 14700,
  },
  {
    id: 'd',
    family: 'cloudB',
    bx: -0.3,
    by: 0.18,
    sx: 1.2,
    sy: 0.82,
    spin: -1,
    seed: 59,
    rough: 0.36,
    alphaBias: -0.05,
    periodMs: 11800,
    periodMsY: 8900,
    swayPeriodMs: 16200,
  },
  {
    id: 'b',
    family: 'cloudB',
    bx: 0.27,
    by: -0.31,
    sx: 0.94,
    sy: 1.06,
    spin: -1,
    seed: 21,
    rough: 0.44,
    alphaBias: 0.06,
    periodMs: 8400,
    periodMsY: 10100,
    swayPeriodMs: 12900,
  },
  {
    id: 'c',
    family: 'cloudA',
    bx: 0.22,
    by: 0.29,
    sx: 0.86,
    sy: 0.78,
    spin: 1,
    seed: 43,
    rough: 0.38,
    alphaBias: 0.06,
    periodMs: 10800,
    periodMsY: 7600,
    swayPeriodMs: 15400,
  },
];

const COMPACT_TEMPLATES: FormationTemplate[] = [
  {
    id: 'a',
    family: 'cloudC',
    bx: -0.14,
    by: -0.1,
    sx: 1.42,
    sy: 1.3,
    spin: 1,
    seed: 7,
    rough: 0.32,
    alphaBias: -0.12,
    periodMs: 16800,
    periodMsY: 19400,
    swayPeriodMs: 22100,
  },
  {
    id: 'b',
    family: 'cloudB',
    bx: 0.26,
    by: 0.16,
    sx: 0.72,
    sy: 0.8,
    spin: -1,
    seed: 21,
    rough: 0.3,
    alphaBias: 0.02,
    periodMs: 15200,
    periodMsY: 18100,
    swayPeriodMs: 20300,
  },
  {
    id: 'c',
    family: 'cloudA',
    bx: -0.1,
    by: 0.27,
    sx: 0.52,
    sy: 0.48,
    spin: 1,
    seed: 43,
    rough: 0.26,
    alphaBias: 0.04,
    periodMs: 17600,
    periodMsY: 14900,
    swayPeriodMs: 23600,
  },
];

function fieldWave(elapsedMs: number, periodMs: number, seed: number, phase = 0): number {
  return Math.sin(((elapsedMs / periodMs) * Math.PI * 2) + seed * 0.73 + phase);
}

function fieldWaveCos(elapsedMs: number, periodMs: number, seed: number, phase = 0): number {
  return Math.cos(((elapsedMs / periodMs) * Math.PI * 2) + seed * 1.19 + phase);
}

/** Deterministic 0…1 noise for contour sampling. */
export function nebulaHashNoise(x: number, y: number, seed: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233 + seed * 43.758) * 43758.5453;
  return n - Math.floor(n);
}

/**
 * Pure visual-model frame builder consumed by canvas and static renderers.
 * Elapsed milliseconds drive continuous low-frequency fields — no modulo seams.
 */
export function buildNebulaVisualFrame(params: BuildNebulaFrameParams): NebulaVisualFrame {
  const {
    state, size, elapsedMs, pulseBoost = 0, speechAmp = 0,
  } = params;
  const choreography = getNebulaChoreography(state);
  const motion = getNebulaMotionProfile(state);
  const isCompact = size <= 28;
  const templates = isCompact ? COMPACT_TEMPLATES : FULL_TEMPLATES;

  const { coherence, muted } = choreography;
  const energy = resolveSpeakingEnergy(state, choreography.energy, speechAmp);
  const effectiveLuminosity = Math.min(choreography.luminosity + pulseBoost, 1);
  const radius = size * 0.42;

  const { activeMotion, centerPeriodFactor } = motion;
  // Two sines on an incommensurate ratio, not sin/cos: a quarter-phase pair is
  // a circle, and a circle reads as the whole field sweeping one direction.
  const globalOffsetX = fieldWave(elapsedMs, motion.globalDriftPeriodMs, 83)
    * motion.globalDriftStrength;
  const globalOffsetY = -fieldWave(elapsedMs, motion.globalDriftPeriodMs * 1.61, 97)
    * motion.globalDriftStrength * 0.78;
  const rawCentres = templates.map((template, index) => {
    // Each parcel gathers on its own clock; a shared gather wave makes the
    // whole nebula inhale and exhale in unison, which is instantly readable.
    const gatherFactor = (
      fieldWave(elapsedMs, 9800 + template.seed * 190, template.seed * 1.7) * 0.5 + 0.5
    ) * motion.gatherMul;
    // Long, per-parcel, mixed-handed roaming. Resting offsets alone pin each
    // parcel to one quadrant forever, so some regions stay permanently thin.
    // Roaming is only lightly damped by state: even idle slowly redistributes.
    const roam = FORMATION_ROAM * (0.62 + activeMotion * 0.38);

    return {
      x: template.bx * (1 - gatherFactor)
        + fieldWave(
          elapsedMs,
          template.periodMs * centerPeriodFactor,
          template.seed,
          index * 0.9,
        ) * motion.wanderX * energy * activeMotion
        + fieldWave(
          elapsedMs,
          26_000 + template.seed * 430,
          template.seed * 2.3,
          index * 1.9,
        ) * roam,
      y: template.by * (1 - gatherFactor)
        + template.spin * fieldWaveCos(
          elapsedMs,
          template.periodMsY * centerPeriodFactor,
          template.seed,
          index * 1.4,
        ) * motion.wanderY * energy * activeMotion
        + template.spin * fieldWaveCos(
          elapsedMs,
          33_500 - template.seed * 260,
          template.seed * 1.9,
          index * 0.7,
        ) * roam,
    };
  });
  const rawCentroid = rawCentres.reduce(
    (centroid, centre) => ({
      x: centroid.x + centre.x / rawCentres.length,
      y: centroid.y + centre.y / rawCentres.length,
    }),
    { x: 0, y: 0 },
  );
  const restingCentroid = templates.reduce(
    (centroid, template) => ({
      x: centroid.x + template.bx / templates.length,
      y: centroid.y + template.by / templates.length,
    }),
    { x: 0, y: 0 },
  );

  const formations: NebulaFormation[] = templates.map((template, index) => {
    const centre = rawCentres[index];
    const centerX = (
      centre.x - rawCentroid.x + restingCentroid.x + globalOffsetX
    ) * FORMATION_CONTAINMENT;
    const centerY = (
      centre.y - rawCentroid.y + restingCentroid.y + globalOffsetY
    ) * FORMATION_CONTAINMENT;
    const globalShapePeriodMs = template.swayPeriodMs * motion.shapePeriodMul;
    const internalPeriodMs = template.swayPeriodMs * motion.internalPeriodMul;
    const scalePulse = clamp(
      1 + fieldWave(elapsedMs, globalShapePeriodMs, template.seed, index)
        * motion.scaleAmplitude * energy * activeMotion,
      motion.scaleMin,
      motion.scaleMax,
    );
    // Continuous in luminosity. A binary energy gate used to lump idle and
    // listening into the same brightness, which is why they looked identical.
    const alphaBase = muted
      ? (isCompact ? 0.38 : 0.28)
      : (isCompact ? 0.3 : 0.22) + effectiveLuminosity * 0.62;
    const emergence = fieldWave(
      elapsedMs,
      internalPeriodMs * 0.79,
      template.seed,
      index * 0.83,
    );
    const phase = (
      fieldWave(elapsedMs, internalPeriodMs, template.seed, index * 0.55)
        * motion.phaseAmp
      + fieldWaveCos(elapsedMs, internalPeriodMs * 0.57, template.seed, index)
        * motion.phaseAmpCos
    ) * Math.PI;
    const morph = fieldWave(
      elapsedMs,
      internalPeriodMs * 1.18,
      template.seed,
      index * 1.7,
    ) * energy * activeMotion * 0.35;
    const depth = 0.5;
    const roughPulse = 1 + fieldWave(
      elapsedMs,
      internalPeriodMs * 1.08,
      template.seed,
      index * 2.2,
    ) * 0.28 * energy * activeMotion;

    return {
      id: template.id,
      cloudFamily: template.family,
      centerX,
      centerY,
      scaleX: template.sx * scalePulse * FORMATION_CONTAINMENT,
      scaleY: template.sy * clamp(
        1 + fieldWaveCos(
          elapsedMs,
          globalShapePeriodMs * 0.83,
          template.seed,
          index * 0.7,
        ) * motion.syAmp * energy * activeMotion,
        motion.scaleMin,
        1.15,
      ) * FORMATION_CONTAINMENT,
      rotation: fieldWave(
        elapsedMs,
        globalShapePeriodMs * 1.15,
        template.seed,
        index * 1.1,
      ) * motion.rotationAmp * template.spin,
      alpha: clamp(
        (alphaBase + template.alphaBias + emergence
          * motion.emergenceAmp * energy * activeMotion)
          * (0.88 + coherence * 0.12),
        muted ? 0.16 : 0.12 + effectiveLuminosity * 0.26,
        0.82,
      ),
      phase,
      seed: template.seed,
      contourRoughness: resolveSpeakingContourRoughness(
        state,
        template.rough * roughPulse,
        speechAmp,
      ),
      depth,
      morph,
      swirl: template.spin,
    };
  });

  return {
    formations,
    silhouetteRadius: radius,
    // Concentric. An off-axis light source is a *fixed* asymmetry: it pins one
    // quadrant permanently dark, which reads as a rendering fault rather than
    // as lighting. Depth comes from the symmetric falloff plus roaming fog.
    wellCenterX: 0.5,
    wellCenterY: 0.5,
    wellAlpha: muted ? 0.92 : 1,
    /** Mix toward nebulaWellAtmosphereRgb — tenant depth, never toward white. */
    wellColorLift: muted ? 0.1 : 0.1 + effectiveLuminosity * 0.6,
    cloudColorLift: muted ? 0 : effectiveLuminosity * 0.08,
    innerShadowStrength: muted ? 0.56 : 0.78 - effectiveLuminosity * 0.34,
    coreAlpha: 0,
    specular: {
      centerX: -0.36,
      centerY: -0.38,
      radiusX: isCompact ? 0.12 : 0.14,
      radiusY: isCompact ? 0.065 : 0.075,
      alpha: 0,
    },
    rimAlpha: muted ? 0.48 : 0.62 + effectiveLuminosity * 0.22,
    // Glass does not emit. Outward bloom is reserved for the speaking punch on
    // the glow shell; the nebula's own light stays inside the silhouette.
    haloAlpha: 0,
    luminosity: effectiveLuminosity,
    elapsedMs,
    isCompact,
  };
}
